/**
 * Master state and territory mapping for the United States
 * Single source of truth for all state-related data across the application
 */

export interface StateInfo {
  readonly abbreviation: string;
  readonly fullName: string;
  readonly type: 'state' | 'territory' | 'military';
}

/**
 * Master list of all US states, territories, and military codes
 * This is the single source of truth for state data
 */
export const MASTER_STATE_LIST: readonly StateInfo[] = [
  // US States
  { abbreviation: 'AL', fullName: 'Alabama', type: 'state' },
  { abbreviation: 'AK', fullName: 'Alaska', type: 'state' },
  { abbreviation: 'AZ', fullName: 'Arizona', type: 'state' },
  { abbreviation: 'AR', fullName: 'Arkansas', type: 'state' },
  { abbreviation: 'CA', fullName: 'California', type: 'state' },
  { abbreviation: 'CO', fullName: 'Colorado', type: 'state' },
  { abbreviation: 'CT', fullName: 'Connecticut', type: 'state' },
  { abbreviation: 'DE', fullName: 'Delaware', type: 'state' },
  { abbreviation: 'DC', fullName: 'District Of Columbia', type: 'state' },
  { abbreviation: 'FL', fullName: 'Florida', type: 'state' },
  { abbreviation: 'GA', fullName: 'Georgia', type: 'state' },
  { abbreviation: 'HI', fullName: 'Hawaii', type: 'state' },
  { abbreviation: 'ID', fullName: 'Idaho', type: 'state' },
  { abbreviation: 'IL', fullName: 'Illinois', type: 'state' },
  { abbreviation: 'IN', fullName: 'Indiana', type: 'state' },
  { abbreviation: 'IA', fullName: 'Iowa', type: 'state' },
  { abbreviation: 'KS', fullName: 'Kansas', type: 'state' },
  { abbreviation: 'KY', fullName: 'Kentucky', type: 'state' },
  { abbreviation: 'LA', fullName: 'Louisiana', type: 'state' },
  { abbreviation: 'ME', fullName: 'Maine', type: 'state' },
  { abbreviation: 'MD', fullName: 'Maryland', type: 'state' },
  { abbreviation: 'MA', fullName: 'Massachusetts', type: 'state' },
  { abbreviation: 'MI', fullName: 'Michigan', type: 'state' },
  { abbreviation: 'MN', fullName: 'Minnesota', type: 'state' },
  { abbreviation: 'MS', fullName: 'Mississippi', type: 'state' },
  { abbreviation: 'MO', fullName: 'Missouri', type: 'state' },
  { abbreviation: 'MT', fullName: 'Montana', type: 'state' },
  { abbreviation: 'NE', fullName: 'Nebraska', type: 'state' },
  { abbreviation: 'NV', fullName: 'Nevada', type: 'state' },
  { abbreviation: 'NH', fullName: 'New Hampshire', type: 'state' },
  { abbreviation: 'NJ', fullName: 'New Jersey', type: 'state' },
  { abbreviation: 'NM', fullName: 'New Mexico', type: 'state' },
  { abbreviation: 'NY', fullName: 'New York', type: 'state' },
  { abbreviation: 'NC', fullName: 'North Carolina', type: 'state' },
  { abbreviation: 'ND', fullName: 'North Dakota', type: 'state' },
  { abbreviation: 'OH', fullName: 'Ohio', type: 'state' },
  { abbreviation: 'OK', fullName: 'Oklahoma', type: 'state' },
  { abbreviation: 'OR', fullName: 'Oregon', type: 'state' },
  { abbreviation: 'PA', fullName: 'Pennsylvania', type: 'state' },
  { abbreviation: 'RI', fullName: 'Rhode Island', type: 'state' },
  { abbreviation: 'SC', fullName: 'South Carolina', type: 'state' },
  { abbreviation: 'SD', fullName: 'South Dakota', type: 'state' },
  { abbreviation: 'TN', fullName: 'Tennessee', type: 'state' },
  { abbreviation: 'TX', fullName: 'Texas', type: 'state' },
  { abbreviation: 'UT', fullName: 'Utah', type: 'state' },
  { abbreviation: 'VT', fullName: 'Vermont', type: 'state' },
  { abbreviation: 'VA', fullName: 'Virginia', type: 'state' },
  { abbreviation: 'WA', fullName: 'Washington', type: 'state' },
  { abbreviation: 'WV', fullName: 'West Virginia', type: 'state' },
  { abbreviation: 'WI', fullName: 'Wisconsin', type: 'state' },
  { abbreviation: 'WY', fullName: 'Wyoming', type: 'state' },
  
  // US Territories
  { abbreviation: 'AS', fullName: 'American Samoa', type: 'territory' },
  { abbreviation: 'GU', fullName: 'Guam', type: 'territory' },
  { abbreviation: 'MP', fullName: 'Northern Mariana Islands', type: 'territory' },
  { abbreviation: 'PR', fullName: 'Puerto Rico', type: 'territory' },
  { abbreviation: 'UM', fullName: 'United States Minor Outlying Islands', type: 'territory' },
  { abbreviation: 'VI', fullName: 'Virgin Islands', type: 'territory' },
  
  // Military
  { abbreviation: 'AA', fullName: 'Armed Forces Americas', type: 'military' },
  { abbreviation: 'AP', fullName: 'Armed Forces Pacific', type: 'military' },
  { abbreviation: 'AE', fullName: 'Armed Forces Others', type: 'military' }
] as const;

/**
 * Performance-optimized lookup maps for O(1) state operations
 */
const ABBREVIATION_TO_FULL_NAME_MAP = new Map(
  MASTER_STATE_LIST.map(s => [s.abbreviation, s.fullName])
);

const FULL_NAME_TO_ABBREVIATION_MAP = new Map(
  MASTER_STATE_LIST.map(s => [s.fullName, s.abbreviation])
);

const ABBREVIATION_SET = new Set(MASTER_STATE_LIST.map(s => s.abbreviation));

/**
 * Utility class for state-related operations
 */
export class StateUtilities {
  /**
   * Get the full name of a state from its abbreviation
   * @param abbreviation - The state abbreviation (e.g., 'CA', 'OR')
   * @returns The full state name or the original abbreviation if not found
   */
  static getFullName(abbreviation: string): string {
    return ABBREVIATION_TO_FULL_NAME_MAP.get(abbreviation) || abbreviation;
  }

  /**
   * Get the abbreviation of a state from its full name
   * @param fullName - The full state name (e.g., 'California', 'Oregon')
   * @returns The state abbreviation or the original name if not found
   */
  static getAbbreviation(fullName: string): string {
    return FULL_NAME_TO_ABBREVIATION_MAP.get(fullName) || fullName;
  }

  /**
   * Check if a given abbreviation is a valid US state/territory
   * @param abbreviation - The abbreviation to validate
   * @returns True if valid, false otherwise
   */
  static isValidState(abbreviation: string): boolean {
    return ABBREVIATION_SET.has(abbreviation);
  }

  /**
   * Get all states of a specific type
   * @param type - The type of location ('state', 'territory', 'military')
   * @returns Array of states matching the type
   */
  static getByType(type: StateInfo['type']): readonly StateInfo[] {
    return Object.freeze(MASTER_STATE_LIST.filter(s => s.type === type));
  }

  /**
   * Get only US states (excludes territories and military)
   * @returns Array of US states only
   */
  static getStatesOnly(): readonly StateInfo[] {
    return StateUtilities.getByType('state');
  }
}
